import React, { useState, useEffect } from "react";
import Loader from "Loader";
import Can from "Can";
import { fetch, toArgList } from "graphql-helper";
import Button from "Button";
import Modal from "Modal";
import { Formik, Form, Field } from "formik";
import TextInput from "form-controls/TextInput";
import "./SiteAdminForm.css";

export default function ParkingInstructionsForm({ selectedSite }) {
  const [parkingInstruction, setParkingInstruction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [modals, setModals] = useState({
    instructionModal: false,
    deleteConfirmation: false
  });

  useEffect(() => {
    loadData();
  }, [selectedSite && selectedSite.id]);

  const loadData = async () => {
    if (!selectedSite || !selectedSite.id) return;

    setIsLoading(true);
    try {
      const { parkingInstructions } = await fetch(`
        query {
          parkingInstructions {
            id
            destination
            destinationColour
            imageBase64
            site {
              id
              name
            }
          }
        }
      `);
      const instruction = parkingInstructions.find(
        pi => pi.site.id === selectedSite.id
      );
      setParkingInstruction(instruction || null);
    } catch (error) {
      console.error("Failed to load parking instructions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const createParkingInstruction = async args => {
    try {
      await fetch(`
        mutation {
          createParkingInstruction(${toArgList(args)}) {
            id
          }
        }
      `);
      await loadData();
      return { success: true };
    } catch (errors) {
      console.error("Failed to create parking instruction:", errors);
      let errorMessage = "Failed to create parking instruction";
      if (Array.isArray(errors) && errors.length > 0) {
        errorMessage = errors
          .map(e => {
            if (e.message === "has already been taken") {
              return "Instruction for this site has already been created.";
            }
            return e.message;
          })
          .join(", ");
      } else if (errors && errors.message) {
        errorMessage = errors.message;
      }
      return { success: false, error: errorMessage };
    }
  };

  const updateParkingInstruction = async args => {
    try {
      await fetch(`
        mutation {
          updateParkingInstruction(${toArgList(args)}) {
            id
          }
        }
      `);
      await loadData();
      return { success: true };
    } catch (errors) {
      console.error("Failed to update parking instruction:", errors);
      let errorMessage = "Failed to update parking instruction";
      if (Array.isArray(errors) && errors.length > 0) {
        errorMessage = errors.map(e => e.message).join(", ");
      } else if (errors && errors.message) {
        errorMessage = errors.message;
      }
      return { success: false, error: errorMessage };
    }
  };

  const deleteParkingInstruction = async id => {
    await fetch(`
      mutation {
        deleteParkingInstruction(id: ${id}) {
          id
        }
      }
    `);
    await loadData();
  };

  const openModal = modalType => {
    setModals(prev => ({
      ...prev,
      [modalType]: true
    }));
  };

  const closeModal = modalType => {
    setModals(prev => ({
      ...prev,
      [modalType]: false
    }));
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    await deleteParkingInstruction(parkingInstruction.id);
    closeModal("deleteConfirmation");
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="admin-main">
        <Loader />
      </div>
    );
  }

  return (
    <div className="admin-main">
      {modals.instructionModal && (
        <InstructionModal
          onClose={() => closeModal("instructionModal")}
          createParkingInstruction={createParkingInstruction}
          updateParkingInstruction={updateParkingInstruction}
          selectedInstruction={parkingInstruction}
          siteId={selectedSite.id}
          siteName={selectedSite.name}
        />
      )}

      {modals.deleteConfirmation && (
        <Modal>
          <div className="modal">
            <div className="modal__content">
              <div className="modal__header">
                <span className="modal__title">Confirm Delete</span>
                <i
                  className="material-icons modal__close"
                  onClick={() => closeModal("deleteConfirmation")}
                >
                  close
                </i>
              </div>
              <div className="modal__body">
                <p>
                  Are you sure you want to delete the parking instruction for{" "}
                  <strong>
                    {selectedSite.name} -{" "}
                    {parkingInstruction && parkingInstruction.destination}
                  </strong>
                  ?
                </p>
                <p>This action cannot be undone.</p>

                <div className="modal__buttons">
                  <Button
                    type="button"
                    variant="secondary"
                    size="tiny"
                    title="Cancel"
                    onClick={() => closeModal("deleteConfirmation")}
                  />
                  <Button
                    type="button"
                    variant="red"
                    size="tiny"
                    title="Delete"
                    onClick={confirmDelete}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      <h3>Parking Instructions for {selectedSite.name}</h3>

      {parkingInstruction ? (
        <div>
          <table>
            <thead>
              <tr>
                <th>Destination</th>
                <th>Image</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{parkingInstruction.destination}</td>
                <td>
                  {parkingInstruction.imageBase64 ? (
                    <img
                      src={parkingInstruction.imageBase64}
                      alt="Parking instruction"
                      style={{
                        maxWidth: "100px",
                        maxHeight: "100px",
                        objectFit: "contain"
                      }}
                    />
                  ) : (
                    "No image"
                  )}
                </td>
                <td>
                  <Can when="settings.parkinginstructions.create">
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <Button
                        variant="primary"
                        size="tiny"
                        title="Update"
                        onClick={() => openModal("instructionModal")}
                      />
                      <Button
                        variant="red"
                        size="tiny"
                        title="Delete"
                        onClick={() => openModal("deleteConfirmation")}
                      />
                    </div>
                  </Can>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div>
          <p>No parking instruction configured for this site.</p>
          <Can when="settings.parkinginstructions.create">
            <Button
              variant="secondary"
              size="small"
              title="Create parking instruction"
              onClick={() => openModal("instructionModal")}
            />
          </Can>
        </div>
      )}
    </div>
  );
}

function InstructionModal({
  onClose,
  createParkingInstruction,
  updateParkingInstruction,
  selectedInstruction,
  siteId,
  siteName
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    selectedInstruction ? selectedInstruction.imageBase64 : null
  );
  const [imageData, setImageData] = useState(null);
  const isEditMode = !!selectedInstruction;

  const resizeImage = (base64String, callback) => {
    const MAX_SIZE_KB = 512;
    const MAX_SIZE_BYTES = MAX_SIZE_KB * 1024;

    // Check if already under limit
    if (base64String.length <= MAX_SIZE_BYTES) {
      callback(base64String);
      return;
    }

    const img = new Image();
    img.onload = () => {
      let width = img.width;
      let height = img.height;
      let quality = 0.8;

      // Function to compress
      const compress = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        const compressed = canvas.toDataURL("image/jpeg", quality);

        // Check if size is acceptable
        if (compressed.length <= MAX_SIZE_BYTES) {
          callback(compressed);
        } else if (quality > 0.1) {
          // Reduce quality
          quality -= 0.1;
          compress();
        } else if (width > 100 || height > 100) {
          // Reduce dimensions if quality is already low
          width = Math.floor(width * 0.8);
          height = Math.floor(height * 0.8);
          quality = 0.8;
          compress();
        } else {
          // Final fallback
          callback(compressed);
        }
      };

      compress();
    };
    img.src = base64String;
  };

  const handleImageChange = e => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        resizeImage(base64String, resizedBase64 => {
          setImagePreview(resizedBase64);
          setImageData(resizedBase64);
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageData("");
  };

  return (
    <Modal>
      <div className="modal">
        <div className="modal__content">
          <div className="modal__header">
            <span className="modal__title">
              {isEditMode
                ? "Update parking instruction"
                : "Create parking instruction"}
            </span>
            <i className="material-icons modal__close" onClick={onClose}>
              close
            </i>
          </div>
          <div className="modal__body">
            {isLoading && <Loader />}

            {error && (
              <div style={{ color: "red", marginBottom: "1rem" }}>{error}</div>
            )}

            {!isLoading && (
              <Formik
                enableReinitialize
                initialValues={{
                  destination: selectedInstruction
                    ? selectedInstruction.destination
                    : "",
                  destinationColour: selectedInstruction
                    ? selectedInstruction.destinationColour || "#000000"
                    : "#000000",
                  ...(selectedInstruction && { id: selectedInstruction.id })
                }}
                onSubmit={async values => {
                  setError(null);
                  setIsLoading(true);

                  const submitValues = {
                    ...values,
                    siteId: String(siteId),
                    ...(imageData !== null && { imageBase64: imageData })
                  };

                  const result = isEditMode
                    ? await updateParkingInstruction(submitValues)
                    : await createParkingInstruction(submitValues);

                  setIsLoading(false);
                  if (result.success) {
                    onClose();
                  } else {
                    setError(result.error);
                  }
                }}
              >
                {({ values, setFieldValue }) => (
                  <Form>
                    <div>
                      <div className="form-control">
                        <label>Site</label>
                        <div
                          style={{ padding: "0.5rem 0", fontWeight: "bold" }}
                        >
                          {siteName}
                        </div>
                      </div>
                      <Field
                        name="destination"
                        label="Destination"
                        component={TextInput}
                      />
                      <div className="form-control">
                        <label>Destination Box Colour</label>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem"
                          }}
                        >
                          <input
                            type="color"
                            value={values.destinationColour}
                            onChange={e =>
                              setFieldValue("destinationColour", e.target.value)
                            }
                            style={{
                              width: "50px",
                              height: "35px",
                              padding: "0",
                              border: "1px solid #ccc",
                              borderRadius: "4px",
                              cursor: "pointer"
                            }}
                          />
                          <span>{values.destinationColour}</span>
                        </div>
                      </div>
                      <div className="form-control">
                        <label>Image</label>
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            style={{ marginBottom: "0.5rem" }}
                          />
                          {imagePreview && (
                            <div style={{ marginTop: "0.5rem" }}>
                              <img
                                src={imagePreview}
                                alt="Preview"
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "200px",
                                  objectFit: "contain"
                                }}
                              />
                              <div style={{ marginTop: "0.5rem" }}>
                                <Button
                                  type="button"
                                  variant="red"
                                  size="tiny"
                                  title="Remove image"
                                  onClick={handleRemoveImage}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="modal__buttons">
                      <Button
                        type="button"
                        variant="secondary"
                        size="tiny"
                        title="Cancel"
                        onClick={onClose}
                      />
                      <Button
                        type="submit"
                        variant="primary"
                        size="tiny"
                        title={isEditMode ? "Update" : "Create"}
                        disabled={!values.destination || !imagePreview}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
